import { useEffect, useState } from "react";
import { FormEvent } from "react";
import common from "../utils/common";

import "bootstrap/dist/css/bootstrap.min.css";
import {
  Form,
  Button,
  Container,
  Row,
  Col,
  FloatingLabel,
} from "react-bootstrap";
import { AuthFunctions } from "../utils/FrontendAuthFunctions";
import { AxiosResponse } from "axios";
import { SystemMsg } from "../utils/SystemMsg";
import { useNavigate } from "react-router";
import logo from "../logo.png";
import ForgotPasswordModal from "../components/Modal/ForgotPasswordModal";
import { BackendApi } from "../utils/BackendApi";
import { ApiRoute } from "../utils/ApiRoute";
import toast, { Toaster } from "react-hot-toast";
import { t } from "../i18n";

const EMAIL_MAX = 254;
const PASSWORD_MAX = 128;
const EMAIL_PATTERN = /^[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,}$/;

type FieldProp = {
  error: string;
  onChange: () => void;
};

function formString(data: FormData, key: string): string {
  const value = data.get(key);
  return typeof value === "string" ? value : "";
}

function loginFieldErrors(email: string, password: string): { email: string; password: string } {
  const emailError =
    common.stringIsNullOrEmpty(email) ||
    email.length > EMAIL_MAX ||
    !EMAIL_PATTERN.test(email)
      ? SystemMsg.Login.email_invalid
      : "";
  const passwordError =
    password.trim().length === 0 || password.length > PASSWORD_MAX
      ? SystemMsg.Login.password_empty
      : "";
  return { email: emailError, password: passwordError };
}

function EmailInput({ error, onChange }: FieldProp) {
  return (
    <Form.Group>
      <FloatingLabel
        controlId="floatingInput"
        label={t("fields.email")}
        className="mb-3"
      >
        <Form.Control
          name="email"
          type="email"
          placeholder={t("fields.email")}
          onChange={onChange}
          isInvalid={error.length > 0}
          required
        />
        <Form.Control.Feedback type="invalid">
          {error}
        </Form.Control.Feedback>
      </FloatingLabel>
    </Form.Group>
  );
}

function PasswordInput({ error, onChange }: FieldProp) {
  return (
    <Form.Group>
      <FloatingLabel controlId="floatingPassword" label={t("fields.password")}>
        <Form.Control
          name="password"
          type="password"
          placeholder={t("fields.password")}
          onChange={onChange}
          isInvalid={error.length > 0}
          required
        />
        <Form.Control.Feedback type="invalid">
          {error}
        </Form.Control.Feedback>
      </FloatingLabel>
    </Form.Group>
  );
}

export default function Login() {
  const navigate = useNavigate();
  const [showLogin, setShowLogin] = useState<boolean>(false);
  const [emailError, setEmailError] = useState<string>("");
  const [passwordError, setPasswordError] = useState<string>("");
  const [showForgotModal, setShowForgotModal] = useState<boolean>(false);

  useEffect(() => {
    const onLoad = async () => {
      const onAuthSuccess = () => {
        setShowLogin(false);
        navigate("/home");
      };

      const onAuthFail = () => {
        setShowLogin(true);
      };
      await AuthFunctions.auth(onAuthSuccess, onAuthFail);
    };
    onLoad();
  }, []);

  const onLoginSuccessful = (res: AxiosResponse) => {
    setEmailError("");
    setPasswordError("");
    navigate("/home");
  };

  const onLoginFailed = (res: AxiosResponse | string) => {
    setPasswordError(SystemMsg.Login.password_incorrect);
  };

  const onSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const data = new FormData(event.currentTarget);
    const email = formString(data, "email").trim();
    const passwordValue = formString(data, "password");
    const errors = loginFieldErrors(email, passwordValue);

    setEmailError(errors.email);
    setPasswordError(errors.password);
    if (errors.email || errors.password) {
      return;
    }

    await AuthFunctions.login(
      email,
      passwordValue,
      onLoginSuccessful,
      onLoginFailed,
    );
  };
  const onForgotPasswordClick = () => {
    setShowForgotModal(true);
  };

  const onResetButtonClick = async (dataObj: { email: string }) => {
    const onResetSuccess = (res: AxiosResponse) => {
      toast.success(t("login.resetEmailSent"));
    };
    const onResetFailed = (res: AxiosResponse | string) => {
      toast.error(t("login.resetEmailFailed"));
    };
    BackendApi.post(ApiRoute.reset, dataObj, onResetSuccess, onResetFailed);
  };

  return (
    <>
      {showLogin && (
        <Container>
          <title>Login - Calendar App</title>
          <Form onSubmit={onSubmit} noValidate>
            <Row className="mt-5"></Row>
            <Row className="mt-5"></Row>
            <Row className="mt-5"></Row>
            <Row className="mt-5"></Row>
            <Row className="justify-content-md-center">
              <Col md={6}>
                <h2 className="mb-3 text-center" style={{ userSelect: "none" }}>
                  <img
                    src={logo}
                    width="32"
                    height="32"
                    className="d-inline-block align-mid mx-3"
                    alt={t("meta.appName")}
                  />
                  <span>{t("meta.appName")}</span>
                </h2>
                <EmailInput
                  error={emailError}
                  onChange={() => setEmailError("")}
                />
                <PasswordInput
                  error={passwordError}
                  onChange={() => setPasswordError("")}
                />
                <Row>
                  <Col style={{ textAlign: "right" }}>
                    <Button
                      variant="link"
                      onClick={onForgotPasswordClick}
                      style={{ color: "grey" }}
                    >
                      {t("login.forgotPassword")}
                    </Button>
                  </Col>
                </Row>
                <div className="d-grid gap-2 mt-3">
                  <Button variant="secondary" size="lg" type="submit">
                    {t("login.login")}
                  </Button>
                </div>
              </Col>
            </Row>
          </Form>
          <ForgotPasswordModal
            show={showForgotModal}
            onResetButtonClick={onResetButtonClick}
            onClose={() => setShowForgotModal(false)}
          />
          <Toaster
            position="top-center"
            reverseOrder={false}
            toastOptions={{
              className: "",
              style: common.toasterStyle,
            }}
          />
        </Container>
      )}
    </>
  );
}
